import { useEffect, useRef } from 'react';
import type { Side } from '@zongheng/core-schema';

export type MatrixOrbMode = 'speaking' | 'idle' | 'prep' | 'thinking';

export interface MatrixOrbProps {
  /** 兼容旧调用：true = speaking，false = idle */
  active?: boolean;
  /**
   * 状态语义（对齐 RareUI MatrixOrb）：
   * - speaking：快速径向波纹 + 高亮；
   * - idle：低频呼吸、点阵坍缩；
   * - prep/thinking：缓慢涟漪 + 中心冷光，表示读卡/计算中。
   */
  mode?: MatrixOrbMode;
  accent?: Side | 'neutral';
  className?: string;
  width?: number;
  height?: number;
  opacity?: number;
  /** 点阵间距（px），默认 7；越小越精密 */
  spacing?: number;
  /** 期望点阵数量（可选）：按面积换算点距，便于按场景控制密度 */
  particles?: number;
}

const ACCENT_RGB: Record<'AFF' | 'NEG' | 'neutral', string> = {
  AFF: '125, 211, 252',
  NEG: '253, 164, 175',
  neutral: '226, 232, 240',
};

/**
 * Matrix Orb —— 真实 <canvas> 点阵核心（非纯 CSS 圆形）。
 *
 * 实现参考 RareUI 的 MatrixOrb 设计语言：**被裁剪成圆形的点阵网格，随状态呼吸 / 涟漪 / 思考**，
 * 以及 React Bits 背景类组件的景深与体积光处理。这里是独立实现，不引入任何组件库。
 */
export function MatrixOrb({
  active = false,
  mode,
  accent = 'neutral',
  className,
  width = 280,
  height = 240,
  opacity = 1,
  spacing = 7,
  particles,
}: MatrixOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const resolvedMode: MatrixOrbMode = mode ?? (active ? 'speaking' : 'idle');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const dpr = Math.min(2, globalThis.devicePixelRatio || 1);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);

    const accentRgb = ACCENT_RGB[accent === 'AFF' ? 'AFF' : accent === 'NEG' ? 'NEG' : 'neutral'];
    const centerX = width * 0.56;
    const centerY = height * 0.5;
    const baseRadius = Math.min(width, height) * (resolvedMode === 'speaking' ? 0.37 : 0.31);

    // 生成被圆裁剪的点阵（每点带随机相位，避免机械同相）
    interface Dot {
      x: number;
      y: number;
      distance: number;
      angle: number;
      phase: number;
    }
    // 若调用方给出期望粒子数，按圆面积换算点距；否则使用显式 spacing
    const effectiveSpacing =
      particles && particles > 0
        ? Math.max(4, Math.min(14, Math.sqrt((Math.PI * baseRadius * baseRadius) / particles)))
        : spacing;
    const dots: Dot[] = [];
    const half = Math.ceil(baseRadius / effectiveSpacing) + 1;
    for (let gx = -half; gx <= half; gx += 1) {
      for (let gy = -half; gy <= half; gy += 1) {
        const offsetX = gy % 2 === 0 ? 0 : effectiveSpacing * 0.5;
        const x = gx * effectiveSpacing + offsetX;
        const y = gy * effectiveSpacing;
        const distance = Math.sqrt(x * x + y * y);
        if (distance > baseRadius) continue;
        dots.push({
          x,
          y,
          distance,
          angle: Math.atan2(y, x),
          phase: Math.random() * Math.PI * 2,
        });
      }
    }

    let frame = 0;
    let raf = 0;
    let t = Math.random() * 100;

    const draw = () => {
      t += resolvedMode === 'speaking' ? 0.055 : resolvedMode === 'idle' ? 0.012 : 0.026;
      const breath =
        resolvedMode === 'idle'
          ? 1 + Math.sin(t * 0.9) * 0.02
          : resolvedMode === 'prep' || resolvedMode === 'thinking'
            ? 1 + Math.sin(t * 1.1) * 0.035
            : 1 + Math.sin(t * 1.6) * 0.045;
      const radius = baseRadius * breath;

      context.clearRect(0, 0, width, height);

      // 中心体积光（激活/思考态）
      if (resolvedMode !== 'idle') {
        const glow = context.createRadialGradient(centerX, centerY, 2, centerX, centerY, radius * 1.55);
        glow.addColorStop(0, 'rgba(255,255,255,0.16)');
        glow.addColorStop(0.45, `rgba(${accentRgb},0.07)`);
        glow.addColorStop(1, 'rgba(4,5,7,0)');
        context.fillStyle = glow;
        context.beginPath();
        context.arc(centerX, centerY, radius * 1.55, 0, Math.PI * 2);
        context.fill();
      }

      // 外圈精密刻度（每 30° 一个）
      context.strokeStyle = 'rgba(255,255,255,0.14)';
      context.lineWidth = 1;
      for (let mark = 0; mark < 24; mark += 1) {
        const angle = (mark / 24) * Math.PI * 2 + t * 0.02;
        const inner = radius * 1.06;
        const outer = radius * (mark % 6 === 0 ? 1.14 : 1.1);
        context.beginPath();
        context.moveTo(centerX + Math.cos(angle) * inner, centerY + Math.sin(angle) * inner);
        context.lineTo(centerX + Math.cos(angle) * outer, centerY + Math.sin(angle) * outer);
        context.stroke();
      }

      // 点阵
      for (const dot of dots) {
        const nx = dot.x / radius;
        const ny = dot.y / radius;
        const edgeFade = Math.max(0, 1 - Math.pow(Math.hypot(nx, ny), 3));
        let alpha: number;
        if (resolvedMode === 'speaking') {
          const ripple = Math.sin(t * 2.2 - dot.distance * 0.32 + dot.phase * 0.25);
          alpha = 0.12 + Math.max(0, ripple) * 0.62 * edgeFade;
        } else if (resolvedMode === 'prep' || resolvedMode === 'thinking') {
          const ripple = Math.sin(t * 0.9 - dot.distance * 0.2);
          alpha = 0.08 + Math.max(0, ripple) * 0.3 * edgeFade;
        } else {
          alpha = (0.05 + 0.09 * Math.sin(t * 0.7 + dot.phase)) * (0.3 + edgeFade * 0.7);
        }
        const isAccentDot = resolvedMode !== 'idle' && (dot.distance < radius * 0.72) && Math.sin(dot.phase + t * 1.4) > 0.965;
        context.fillStyle = isAccentDot
          ? `rgba(${accentRgb}, ${Math.min(0.95, alpha + 0.25)})`
          : `rgba(248, 250, 252, ${Math.min(0.92, alpha)})`;
        const size = resolvedMode === 'speaking' ? 1.15 : 1;
        context.beginPath();
        context.arc(centerX + dot.x, centerY + dot.y, size, 0, Math.PI * 2);
        context.fill();
      }

      // 中心十字准星
      context.strokeStyle = 'rgba(255,255,255,0.28)';
      context.beginPath();
      context.moveTo(centerX - 4, centerY);
      context.lineTo(centerX + 4, centerY);
      context.moveTo(centerX, centerY - 4);
      context.lineTo(centerX, centerY + 4);
      context.stroke();

      if (reduceMotion) return;
      frame += 1;
      // idle 状态降到 ~30fps，减少常驻 GPU/CPU 占用
      if (resolvedMode === 'idle' && frame % 2 !== 0) {
        raf = requestAnimationFrame(draw);
        return;
      }
      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(raf);
  }, [resolvedMode, accent, width, height, spacing, particles]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
      style={{ width, height, opacity, pointerEvents: 'none' }}
    />
  );
}
